import MenuItem from '@mui/material/MenuItem'
import MuiSelect, { type SelectChangeEvent } from '@mui/material/Select'
import { useCallback, useContext, useId, useMemo, useRef, type Ref } from 'react'

import { cn } from './cn'
import { markEdited } from './edited'
import { FieldError } from './FieldError'
import { FieldLabelContext } from './field-label'

export interface SelectOption<T extends string | number> {
  readonly value: T
  readonly label: string
}

export interface SelectProps<T extends string | number> {
  /** Left out (or empty) inside a `FormRow`, whose label then names the field. */
  readonly label?: string
  /** Follows the row's label when a `FormRow` holds several fields ("Duration" → "Duration unit"). */
  readonly labelSuffix?: string
  /** Keeps the label for assistive technology only. */
  readonly hideLabel?: boolean
  readonly value: T
  readonly onChange: (value: T) => void
  /** Plain strings are their own labels. */
  readonly options: readonly (SelectOption<T> | (T & string))[]
  readonly size?: 'small' | 'medium'
  /** The error message; when set, the field shows its error state and names the message. */
  readonly error?: string
  /** The id of a hint that describes the field, read before any error. */
  readonly describedBy?: string
  readonly disabled?: boolean
  /**
   * The viewer may not change it: it cannot open, but stays focusable and readable, drawn with
   * the read-only fill TextField's `isLocked` has.
   */
  readonly isLocked?: boolean
  readonly className?: string
  /** The options' language, when it differs from the page's: the menu renders outside it. */
  readonly lang?: string
  /** For a form library: the field's name, blur handler and a ref that can focus it. */
  readonly name?: string
  readonly onBlur?: () => void
  readonly inputRef?: Ref<unknown>
}

/**
 * The archetype's select: an outlined field with the label as a caption above it. The menu
 * renders in a portal, so a scrolling ancestor such as a table does not clip it.
 */
export function Select<T extends string | number>({
  label,
  labelSuffix,
  hideLabel = false,
  value,
  onChange,
  options,
  size = 'small',
  error,
  describedBy,
  disabled = false,
  isLocked = false,
  className,
  lang,
  name,
  onBlur,
  inputRef,
}: SelectProps<T>) {
  const ownLabelId = useId()
  const rowLabelId = useContext(FieldLabelContext)
  const hasLabel = label !== undefined && label !== ''
  // Its own label first; else the row's label, then its suffix.
  const suffixed = !hasLabel && rowLabelId !== null && labelSuffix !== undefined && labelSuffix !== ''
  const labelId = hasLabel ? ownLabelId : suffixed ? `${rowLabelId} ${ownLabelId}` : (rowLabelId ?? undefined)
  const errorId = useId()
  const menuProps = useMemo(() => (lang === undefined ? undefined : { lang }), [lang])

  const normalized = useMemo(
    () =>
      options.map((option) =>
        typeof option === 'object' ? option : { value: option as T, label: option },
      ),
    [options],
  )

  const field = useRef<HTMLDivElement>(null)
  const handleChange = useCallback(
    (event: SelectChangeEvent<T>) => {
      markEdited(field.current)
      onChange(event.target.value as T)
    },
    [onChange],
  )

  return (
    <div ref={field} className={cn('flex flex-col gap-1', className)}>
      {hasLabel && (
        <span id={ownLabelId} className={hideLabel ? 'sr-only' : 'text-xs font-medium text-text-secondary'}>
          {label}
        </span>
      )}
      {suffixed && (
        <span id={ownLabelId} className="sr-only">
          {labelSuffix}
        </span>
      )}
      <MuiSelect<T>
        labelId={labelId}
        value={value}
        // An empty value shows its option's label, such as "None", rather than a blank field.
        displayEmpty
        onChange={handleChange}
        size={size}
        error={error !== undefined}
        aria-describedby={[describedBy, error === undefined ? undefined : errorId].filter(Boolean).join(' ') || undefined}
        disabled={disabled}
        readOnly={isLocked}
        fullWidth
        MenuProps={menuProps}
        name={name}
        onBlur={onBlur}
        inputRef={inputRef}
      >
        {normalized.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>
      {error !== undefined && <FieldError id={errorId} message={error} />}
    </div>
  )
}
